/**
 * Buttons, fields and toggles (Design System §07–08). One filled button per view; labels in
 * sentence case; no arrows; destructive actions are ink with the consequence in the label.
 */
import type { ChangeEvent, MouseEvent } from "react";
import { h, cx, type ReactNode } from "./h.ts";
import { type Confidence, confidenceWord } from "./marks.ts";

export type ButtonKind = "primary" | "secondary" | "quiet" | "danger" | "link";

export interface ButtonProps {
  label: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  kind?: ButtonKind;
  small?: boolean;
  disabled?: boolean;
  type?: "button" | "submit";
  /** Set for a destructive action; rendered as the second sentence a reader sees. */
  consequence?: string;
  ariaLabel?: string;
}

export function Button({ label, onClick, kind = "secondary", small = false, disabled = false, type = "button", consequence, ariaLabel }: ButtonProps): ReactNode {
  const className = cx(
    kind === "primary" ? "btn" : kind === "link" ? "btn-link" : "btn-2",
    kind === "quiet" && "quiet", kind === "danger" && "btn-danger", small && "btn-sm",
  );
  return h("button", { type, className, onClick, disabled, "aria-label": ariaLabel },
    consequence === undefined ? label : `${label} — ${consequence}`);
}

/** A link drawn as a button, for a route rather than an action. */
export function LinkButton({ label, href, kind = "secondary" }: { label: string; href: string; kind?: "primary" | "secondary" }): ReactNode {
  return h("a", { href, className: kind === "primary" ? "btn" : "btn-2" }, label);
}

export interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  name?: string;
  kind?: "text" | "email" | "tel" | "url" | "number" | "textarea" | "select";
  options?: readonly string[];
  placeholder?: string;
  hint?: string;
  message?: string;
  flagged?: boolean;
  locked?: boolean;
  inputMode?: "numeric" | "email" | "tel" | "text" | "decimal";
  rows?: number;
  /** A help badge rendered after the label, when the field is a key element. */
  badge?: ReactNode;
}

/** Field states: filled = ink, empty = rule, focus = ochre ring, flagged = thicker bottom rule + ochre message, locked = darker ground. */
export function Field(p: FieldProps): ReactNode {
  const empty = p.value === "";
  const className = cx(p.locked && "locked", !p.locked && empty && "empty-field", p.flagged && "flagged");
  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => p.onChange(e.target.value);
  let control: ReactNode;
  if (p.kind === "textarea") {
    control = h("textarea", { name: p.name, value: p.value, onChange, placeholder: p.placeholder, disabled: p.locked, className, rows: p.rows ?? 3 });
  } else if (p.kind === "select") {
    control = h("select", { name: p.name, value: p.value, onChange, disabled: p.locked, className },
      ...(p.options ?? []).map((o) => h("option", { key: o, value: o }, o)));
  } else {
    control = h("input", { type: p.kind ?? "text", name: p.name, value: p.value, onChange, placeholder: p.placeholder, disabled: p.locked, className, inputMode: p.inputMode });
  }
  return h("label", { className: "oc-field" },
    h("span", { className: "oc-label" }, p.label, p.hint === undefined ? null : h("span", { className: "hint" }, ` — ${p.hint}`), p.badge ?? null),
    control,
    p.message === undefined ? null : h("span", { className: "note oc-message" }, p.message),
  );
}

/** Keys 1 · 2 · 3 set confidence; green fills only at 3 (Design System §05). */
export function ConfidenceToggle({ value, onPick, disabled = false, label = "Confidence" }: { value: Confidence | null; onPick: (c: Confidence) => void; disabled?: boolean; label?: string }): ReactNode {
  return h("span", { className: "oc-toggle", role: "radiogroup", "aria-label": label },
    ...([1, 2, 3] as const).map((n) => h("button", {
      key: n, type: "button", role: "radio", className: cx("btn-2", "btn-sm", n === 3 && "is-verified"),
      "aria-checked": value === n ? "true" : "false", "aria-label": `${n} — ${confidenceWord(n)}`, title: confidenceWord(n), disabled,
      onClick: (e: MouseEvent) => { e.stopPropagation(); onPick(n); },
    }, String(n))));
}

/** Two or three options, one pressed (Design System §08 "Segmented"). */
export function Segmented({ options, value, onPick, label }: { options: readonly string[]; value: string; onPick: (v: string) => void; label: string }): ReactNode {
  return h("div", { className: "oc-segmented", role: "group", "aria-label": label },
    ...options.map((o) => h("button", { key: o, type: "button", "aria-pressed": o === value ? "true" : "false", onClick: () => onPick(o) }, o)));
}

/** The intake's one-tap choice list: ruled rows, the chosen one on the darker ground. */
export function ChoiceList({ options, value, onPick }: { options: readonly string[]; value: string | null; onPick: (v: string) => void }): ReactNode {
  return h("div", { className: "oc-choice", role: "group" },
    ...options.map((o) => h("button", { key: o, type: "button", "aria-pressed": o === value ? "true" : "false", onClick: () => onPick(o) }, o)));
}

export function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }): ReactNode {
  return h("label", { style: { display: "flex", gap: "10px", alignItems: "center", minHeight: "var(--oc-control-h)" } },
    h("input", { type: "checkbox", checked, onChange: (e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked), style: { width: "18px", height: "18px" } }),
    label);
}
